import React from 'react';
import { getTitle } from '../../Components/Formatting/Customer/Title';
import { getSubTitle } from '../../Components/Formatting/Customer/Subtitle';
import { classNames, getEntityLink, useDirectionsTo } from '../../Services/helpers';
import { useMapUrl } from '../../Components/Formatting/Map/Map';
import {statuses} from '../../Data/Jobs';
import Icon from '../../Components/Icons/Icon';
import { useNavigate } from 'react-router-dom';
import ButtonRow from '../../Components/Button/ButtonRow';
import Button from '../../Components/Button/Button';
import { useLocationAndTimeForecast } from '../../Data/Weather';
import JobDetails from '../../Components/Formatting/Grids/JobDetails';
import { Roles } from '../../Data/Users';
import { Transition } from '@headlessui/react';
import Tags from '../../Components/Tags/Tags';
import Dropdown from '../../Components/Dropdown';
import { useSession } from '../../Components/Session';

const Job = ({isExpanded, toggleIsExpanded, job, setMarkingJobDone, setSkipping, setScheduling, onChange}) => {

    const navigate = useNavigate();
    const directionsTo = useDirectionsTo();
    const {hasFeature} = useSession();

    // Do this here to pre-fetch the weather and allow the API calls to be batched
    useLocationAndTimeForecast(job.customer, job.scheduledDate, job.estimatedTime);

    const titleClasses = (statuses[job.status].considerDone ?? false) ? 'text-muted line-through' : '';

    return <div className={classNames(
            'pt-4 transition-all duration-300 ease-in-out',
            isExpanded ? 'mb-4' : 'mb-3'
        )}
        >
        <div className="mb-3 flex flex-row items-center justify-between gap-x-3">
            <div>
                <Icon icon={statuses[job.status].icon} size="2x" className={statuses[job.status].colors} />
            </div>
            <div className="grow flex flex-col gap-y-1" onClick={toggleIsExpanded}>
                <strong className={titleClasses}>{getTitle(job.customer)}</strong>
                <span className={titleClasses}>{getSubTitle(job.customer)}</span>
                {hasFeature('TAGS') && <Tags tags={[...job?.customer?.tags ?? [], ...job?.tags ?? []]} muted />}
            </div>
            <div>
                <Dropdown options={[
                        {
                            title: 'Mark as done',
                            icon: 'Check',
                            onClick: () => setMarkingJobDone(job),
                        },
                        {
                            title: 'Directions',
                            icon: 'Route',
                            onClick: () => directionsTo(job.customer.address),
                        },
                        {
                            div: true,
                        },
                        {
                            title: 'Skip job',
                            icon: 'Forward',
                            onClick: () => setSkipping(job),
                        },
                        {
                            title: 'Reschedule job',
                            icon: 'CalendarPlus',
                            onClick: () => setScheduling(job),
                            role: Roles.Manager,
                        },
                        {
                            div: true,
                        },
                        {
                            title: 'View customer',
                            icon: 'User',
                            onClick: () => navigate(getEntityLink('Customers/Customer', job.customer.id)),
                            role: Roles.Manager,
                        }
                    ]}
                />
            </div>
        </div>

        <Transition
            show={isExpanded}
        >
            <div className={classNames(
                // Base styles
                'transition ease-in-out transition-[max-height] transition-opacity duration-500 overflow-hidden max-h-[1000px]',
                // Shared closed styles
                'data-[closed]:opacity-0 data-[closed]:max-h-0',
            )}>
                <ExpandedJob {...{job, customer: job.customer, setMarkingJobDone, onChange}} />
            </div>
        </Transition>

        {/*
        <div className="hidden print:block">
            <JobDetails {...{job, customer: job.customer, onChange}} />
        </div>
        */}
    </div>
}

const ExpandedJob = ({job, customer, setMarkingJobDone, onChange}) => {
    const image = useMapUrl(job.customer, true);
    const directionsTo = useDirectionsTo();

    return <>
        <div className="w-full mb-4 rounded-xl h-[20vh] bg-gray-50 bg-cover bg-center border overflow-hidden" style={{backgroundImage: `url(${image})`}} />

        <JobDetails {...{job, customer, onChange}} />

        <ButtonRow>
            <Button label="Directions" icon="Route" theme="light" onClick={() => directionsTo(customer.address)} />
            <Button label="Mark as done" icon="Check" theme="primary" onClick={() => setMarkingJobDone(job)} />
        </ButtonRow>
    </>
}

export default Job;
